import { z } from 'zod';

import { BaseWidgetSchema } from './BaseWidgetSchema';
import { BorderRadiusThemePropsSchema, BorderThemePropsSchema } from './FlexSectionWidgetStaticSchema';
import { ProseMirrorJSONSchema } from './ProseMirrorJSONSchema';
import { ObjectFit } from './SelectionQuestionWidgetStaticSchema';
import { WidgetType } from '../../utils/widgetsUtils';

export enum GoToType {
  Page = 'page',
  ExternalLink = 'externalLink'
}

export enum ImagePosition {
  start = 'start',
  end = 'end'
}

export enum AccordionChevronPosition {
  start = 'start',
  end = 'end'
}

const BasicImageSchema = z.object({
  alt: z.string().trim().optional(),
  mobileObjectFit: z.nativeEnum(ObjectFit).optional(),
  mobileSrc: z.string().optional(),
  objectFit: z.nativeEnum(ObjectFit).optional(),
  src: z.string().optional()
});

const StrictImageSchema = BasicImageSchema.extend({
  src: z.string().url() // the src is required to be a valid URL
});

const AccordionItemSchema = z.object({
  id: z.string(),
  text: z.object({ fragment: ProseMirrorJSONSchema }).optional(),
  image: BasicImageSchema.optional()
});

export enum VerticalAlignment {
  top = 'top',
  middle = 'middle',
  bottom = 'bottom'
}

export enum HorizontalAlignment {
  start = 'start',
  center = 'center',
  end = 'end'
}

export const AccordionWidgetStaticSchema = BaseWidgetSchema(WidgetType.Accordion)
  .extend({
    items: z.array(AccordionItemSchema),
    headerElements: z.array(z.enum(['Text', 'Image'])),
    transitionDuration: z.number().optional().nullable(),
    style: z.object({
      activeBackgroundColor: z.string().optional().nullable(),
      arrowColor: z.string().optional().nullable(),
      backgroundColor: z.string().optional().nullable(),
      border: BorderThemePropsSchema.optional().nullable(),
      borderRadius: BorderRadiusThemePropsSchema.optional().nullable(),
      chevronPosition: z.nativeEnum(AccordionChevronPosition).optional().nullable(),
      contentBackgroundColor: z.string().optional().nullable(),
      gap: z.number().optional().nullable(),
      horizontalAlignment: z.nativeEnum(HorizontalAlignment).optional().nullable(),
      hoverBackgroundColor: z.string().optional().nullable(),
      imagePosition: z.nativeEnum(ImagePosition).optional().nullable(),
      padding: z.number().optional().nullable(),
      separator: BorderThemePropsSchema.optional().nullable(),
      verticalAlignment: z.nativeEnum(VerticalAlignment).optional().nullable()
    })
  })
  .superRefine((data, ctx) => {
    const includesImage = data.headerElements.includes('Image');

    if (includesImage) {
      data.items.forEach((item, index) => {
        const result = StrictImageSchema.safeParse(item.image);

        if (!result.success) {
          result.error.issues.forEach(issue => {
            ctx.addIssue({ ...issue, path: ['items', index, 'image', ...issue.path] });
          });
        }
      });
    }
  });

export type AccordionWidgetStaticSchemaType = z.infer<typeof AccordionWidgetStaticSchema>;
